import { AUTO_LOGOUT } from "@/config/constants";
import { logger } from "@/helpers/loggerHelper";

type BroadcastChannelEntry = {
  channel: BroadcastChannel;
  refCount: number;
};

const channels = new Map<string, BroadcastChannelEntry>();

export const createBroadcastChannel = (
  channelName = AUTO_LOGOUT.CHANNEL_NAME
): BroadcastChannel | null => {
  try {
    const entry = channels.get(channelName);
    if (entry) {
      entry.refCount += 1;
      return entry.channel;
    }

    const channel = new BroadcastChannel(channelName);
    channels.set(channelName, { channel, refCount: 1 });
    return channel;
  } catch (err) {
    logger.warn("Failed to create broadcast channel", err);
    return null;
  }
};

export const postBroadcastChannelMessage = (
  message: unknown,
  channelName = AUTO_LOGOUT.CHANNEL_NAME,
  usePersistentIfExists = true
): void => {
  try {
    const entry = channels.get(channelName);
    if (usePersistentIfExists && entry && entry.channel) {
      entry.channel.postMessage(message);
      return;
    }

    // Use a temporary channel to avoid creating a persistent one implicitly
    const temp = new BroadcastChannel(channelName);
    temp.postMessage(message);
    temp.close();
  } catch (err) {
    logger.warn("Failed to post broadcast message", err);
  }
};

export const closeBroadcastChannel = (
  channelName = AUTO_LOGOUT.CHANNEL_NAME
): void => {
  try {
    const entry = channels.get(channelName);
    if (!entry) return;
    entry.refCount = Math.max(0, entry.refCount - 1);
    if (entry.refCount === 0) {
      try {
        entry.channel.close();
      } finally {
        channels.delete(channelName);
      }
    }
  } catch (err) {
    logger.warn("Failed to close broadcast channel", err);
  }
};

export const getBroadcastChannelRefCount = (
  channelName = AUTO_LOGOUT.CHANNEL_NAME
): number => channels.get(channelName)?.refCount || 0;
